import type { ComponentProps } from 'react'
import { Badge } from '@/components/ui/badge'
import { RequestState } from '@kfolks/types'

/**
 * Visual mapping for the 8 coarse `RequestState` values. The colour palette
 * is coordinated between this component (cards / detail headers) and
 * `<RequestWorkflowTimeline>`'s status dots so the two renderings agree at
 * a glance.
 *
 * We use Tailwind + `variant: 'outline'` overrides so the core Badge
 * component stays stock. For truly distinct hues (blue, amber, red-outline)
 * we attach a `className` prop with text-/bg-/border- utilities.
 *
 * `approved` (added in `comments-audit-and-ticket-lifecycle`) sits between
 * `in_progress` (in-flight blue) and `completed` (success green) — teal/
 * light-green signals "approved, awaiting execution".
 */
interface StateBadgeMapping {
	variant: ComponentProps<typeof Badge>['variant']
	className?: string
	label: string
}

const STATE_MAPPING: Record<RequestState, StateBadgeMapping> = {
	[RequestState.SUBMITTED]: { variant: 'secondary', label: 'Submitted' },
	[RequestState.IN_PROGRESS]: {
		variant: 'outline',
		className: 'border-blue-300 bg-blue-50 text-blue-700',
		label: 'In progress',
	},
	[RequestState.PENDING_INFO]: {
		variant: 'outline',
		className: 'border-amber-300 bg-amber-50 text-amber-800',
		label: 'Waiting on submitter',
	},
	[RequestState.APPROVED]: {
		variant: 'outline',
		className: 'border-teal-300 bg-teal-50 text-teal-700',
		label: 'Approved',
	},
	[RequestState.COMPLETED]: {
		variant: 'outline',
		className: 'border-green-300 bg-green-50 text-green-700',
		label: 'Completed',
	},
	[RequestState.REJECTED]: { variant: 'destructive', label: 'Rejected' },
	[RequestState.CANCELLED]: { variant: 'secondary', label: 'Cancelled' },
	[RequestState.FAILED]: {
		variant: 'outline',
		className: 'border-red-300 bg-red-50 text-red-700',
		label: 'Failed',
	},
}

export function RequestStateBadge({ state }: { state: RequestState | string }) {
	const m = STATE_MAPPING[state as RequestState]
	if (!m) return <Badge variant="secondary">{state}</Badge>
	return (
		<Badge variant={m.variant} className={m.className}>
			{m.label}
		</Badge>
	)
}
